import React, { useEffect, useState } from "react";
import { Button, Input } from "reactstrap";
import Select from "react-select";
import api from "services/api";
import { makeReactSelectStyles, reactSelectPortalProps } from "utils/reactSelectStyles";
import "./freteSpot.css";

// PO1-3200: "+ Frete Spot" — inclui na PRÓPRIA cotação uma transportadora da
// carteira com o valor negociado por fora (ex.: Hengst × Autogiro/Lumi, que não
// têm tabela nem API e por isso nunca voltam na simulação).
//
// Usado no passo 3 da Nova Cotação e no Editar Cotação. A proposta sai com
// fonte "spot" e entra na mesma lista das propostas de tabela/API.
//
// Só transportadora da carteira (EmbarcadorTransportadora ativo): o
// idTransportadora é o que permite, depois, ligar o valor spot à auditoria do
// CT-e. Texto livre não entra.

// A carteira pode ser grande (Hengst: ~430 transportadoras), por isso select
// com busca. Menu em portal acima do Modal do Editar Cotação (z-index 1050).
const selectStyles = makeReactSelectStyles({ minHeight: 31, radius: 6, portalZIndex: 2000 });

// Valor digitado em pt-BR ("1.234,56", "1.250") ou com ponto decimal
// ("1234.56"). Sem vírgula, ponto seguido de exatamente 3 dígitos é milhar
// ("1.250" = 1250, não 1,25). Negativo não é valor de frete: vira 0.
export const parseValorSpot = (value) => {
  if (String(value || "").includes("-")) return 0;
  const cleaned = String(value || "")
    .replace(/\s/g, "")
    .replace(/[R$]/gi, "")
    .replace(/[^\d.,]/g, "");
  if (!cleaned) return 0;
  let normalized = cleaned;
  if (cleaned.includes(",")) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    normalized = cleaned.replace(/\./g, "");
  } else if ((cleaned.match(/\./g) || []).length > 1) {
    const parts = cleaned.split(".");
    const last = parts.pop();
    normalized = `${parts.join("")}.${last}`;
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
};

const FreteSpotForm = ({ idEmbarcador, onAdd, onCancel }) => {
  const [carteira, setCarteira] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erroCarteira, setErroCarteira] = useState(null);
  const [escolhida, setEscolhida] = useState(null);
  const [valor, setValor] = useState("");
  const [prazo, setPrazo] = useState("");
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!idEmbarcador) {
      setCarteira([]);
      return undefined;
    }
    let mounted = true;
    setLoading(true);
    setErroCarteira(null);
    api
      .get(`/transportadoras/${idEmbarcador}`, { params: { somenteMatriz: 1 } })
      .then((res) => {
        if (!mounted) return;
        const lista = (Array.isArray(res.data) ? res.data : []).map((t) => ({
          value: t.transportadora_id,
          label: t.transportadora_fantasia || `#${t.transportadora_id}`,
        }));
        setCarteira(lista);
      })
      .catch(() => {
        if (mounted) setErroCarteira("Não foi possível carregar as transportadoras.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [idEmbarcador]);

  const handleAdicionar = () => {
    if (!escolhida) {
      setErro("Selecione a transportadora.");
      return;
    }
    const valorNum = Math.round(parseValorSpot(valor) * 100) / 100;
    if (!(valorNum > 0)) {
      setErro("Informe o valor do frete.");
      return;
    }
    const prazoNum = String(prazo).replace(/\D/g, "");
    onAdd({
      transportadora: escolhida.label,
      idTransportadora: escolhida.value,
      valor: valorNum,
      prazo: prazoNum ? Number(prazoNum) : null,
      fonte: "spot",
    });
  };

  return (
    <div className="frete-spot-form">
      <div className="frete-spot-form__campos">
        <div className="frete-spot-form__campo frete-spot-form__campo--transp">
          <label>Transportadora</label>
          <Select
            classNamePrefix="react-select"
            styles={selectStyles}
            {...reactSelectPortalProps()}
            options={carteira}
            value={escolhida}
            isLoading={loading}
            isDisabled={!!erroCarteira}
            placeholder={loading ? "Carregando..." : "Buscar na carteira..."}
            noOptionsMessage={() => "Nenhuma transportadora encontrada"}
            onChange={(opt) => {
              setEscolhida(opt);
              setErro(null);
            }}
          />
        </div>
        <div className="frete-spot-form__campo">
          <label>Valor (R$)</label>
          <Input
            type="text"
            bsSize="sm"
            inputMode="decimal"
            placeholder="0,00"
            value={valor}
            onChange={(e) => {
              setValor(e.target.value);
              setErro(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleAdicionar();
            }}
          />
        </div>
        <div className="frete-spot-form__campo frete-spot-form__campo--prazo">
          <label>Prazo (dias)</label>
          <Input
            type="text"
            bsSize="sm"
            inputMode="numeric"
            placeholder="—"
            value={prazo}
            onChange={(e) => setPrazo(e.target.value.replace(/\D/g, ""))}
          />
        </div>
        <div className="frete-spot-form__acoes">
          <Button color="primary" size="sm" onClick={handleAdicionar} disabled={loading}>
            Incluir
          </Button>
          <Button color="secondary" size="sm" outline onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </div>
      {(erro || erroCarteira) && (
        <div className="frete-spot-form__erro">{erro || erroCarteira}</div>
      )}
      {!loading && !erroCarteira && carteira.length === 0 && idEmbarcador && (
        <div className="frete-spot-form__erro">
          Nenhuma transportadora ativa na carteira deste embarcador.
        </div>
      )}
    </div>
  );
};

export default FreteSpotForm;
